import { WaveCloudAPI, WAVE_APP_ID, chatMentionsAssistant } from "@vida/wave-core";
import type { WaveChatMessage } from "@vida/wave-core";
import { createWaveApiConfig, trackActivity } from "./waveSession";
import { downloadBlobText } from "./waveBlobDownload";
import { WAVE_AGENT_MODEL_ID } from "./waveAgentModel";

const TERMINAL = new Set(["COMPLETED", "FAILED", "CANCELLED", "TIMED_OUT"]);

async function pollAIJob(jobId: string, timeoutMs = 120_000): Promise<string> {
  const cfg = createWaveApiConfig();
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    const job = await WaveCloudAPI.getAIJob(cfg, jobId);
    if (TERMINAL.has(job.status)) {
      if (job.status !== "COMPLETED") {
        throw new Error(job.errorMessage ?? `Job ${job.status}`);
      }
      const key = job.outputAssetKey?.trim();
      if (key) return downloadBlobText(key);
      return "";
    }
    await new Promise((r) => setTimeout(r, 1500));
  }
  throw new Error("Timeout assistente chat.");
}

export async function runWaveChatAgent(params: {
  waveId: string;
  userPrompt: string;
  recentMessages: WaveChatMessage[];
  parentId?: string | null;
}): Promise<{ jobId: string }> {
  const cfg = createWaveApiConfig();
  const transcript = params.recentMessages
    .slice(-12)
    .map((m) => `${m.authorName ?? m.authorSub}: ${m.body}`)
    .join("\n");
  const body = {
    bedrock: {
      systemText:
        "Sei l'assistente Wave nella chat del documento. Rispondi in italiano, conciso e utile. " +
        "Non usare markdown pesante.",
      userText:
        `CRONOLOGIA CHAT RECENTE:\n${transcript || "(vuota)"}\n\n` +
        `MESSAGGIO UTENTE:\n${params.userPrompt}`,
      maxTokens: 2048,
    },
    waveId: params.waveId,
    parentId: params.parentId ?? null,
  };
  const result = await WaveCloudAPI.submitAIJob(cfg, {
    kind: "BEDROCK_CONVERSE",
    provider: "BEDROCK",
    prompt: params.userPrompt,
    paramsJson: JSON.stringify(body),
    inputAssetKeys: [],
    appId: WAVE_APP_ID,
    modelId: WAVE_AGENT_MODEL_ID,
    feature: "wave.agent.chat",
  });
  trackActivity("Chat Wave", `Assistente (job ${result.jobId.slice(0, 8)}…)`);
  return { jobId: result.jobId };
}

export async function maybeTriggerChatAgent(params: {
  waveId: string;
  body: string;
  recentMessages: WaveChatMessage[];
  parentId?: string | null;
}): Promise<void> {
  if (!chatMentionsAssistant(params.body)) return;
  await runWaveChatAgent({
    waveId: params.waveId,
    userPrompt: params.body,
    recentMessages: params.recentMessages,
    parentId: params.parentId,
  });
}

export { pollAIJob, chatMentionsAssistant };
